import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import IssueCard from '@/components/IssueCard';
import IssueMap from '@/components/IssueMap';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, Plus, MapPin, User, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { Issue } from '@/types';
import { storageService } from '@/lib/storage';
import { authService } from '@/lib/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {BarChart,Bar,XAxis,YAxis,Tooltip,ResponsiveContainer,LineChart,Line,CartesianGrid,} from "recharts";
import IssueFeedbackModal from '@/components/IssueFeedbackModal';


const getHighRiskIssues = (issues: Issue[]) =>
  issues.filter(i => (i.riskScore ?? 0) >= 70 && i.status !== 'resolved');
const getCategoryChartData = (issues: Issue[]) => {
  const map: Record<string, number> = {};
  issues.forEach(i => {
    map[i.category] = (map[i.category] || 0) + 1;
  });
  return Object.entries(map).map(([name, value]) => ({
    name: name.replace("_", " "),
    value,
  }));
};

const getStatusChartData = (issues: Issue[]) => {
  const map: Record<string, number> = {};
  issues.forEach(i => {
    map[i.status] = (map[i.status] || 0) + 1;
  });
  return Object.entries(map).map(([name, value]) => ({
    name: name.replace("_", " "),
    value,
  }));
};

const getMonthlyTrendData = (issues: Issue[]) => {
  const map: Record<string, number> = {};
  issues.forEach(i => {
    const month = format(new Date(i.reportedAt), "MMM");
    map[month] = (map[month] || 0) + 1;
  });
  return Object.entries(map).map(([name, value]) => ({
    name,
    value,
  }));
};


export default function AdminDashboard() {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [filteredIssues, setFilteredIssues] = useState<Issue[]>([]);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [showIssueDialog, setShowIssueDialog] = useState(false);
 
  const [showFeedback, setShowFeedback] = useState(false);
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();

  const loadIssues = () => {
    const loadedIssues = storageService.getIssues();
    setIssues(loadedIssues);
    setFilteredIssues(loadedIssues);
  };

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin') {
      navigate('/login');
      return;
    }
    loadIssues();
  }, [currentUser, navigate]);

  useEffect(() => {
    let filtered = issues;

    if (searchTerm) {
      filtered = filtered.filter(issue =>
        issue.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.reportedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.location.address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(issue => issue.status === statusFilter);
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(issue => issue.category === categoryFilter);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(issue => issue.priority === priorityFilter);
    }
    filtered = [...filtered].sort(
  (a, b) => (b.riskScore ?? 0) - (a.riskScore ?? 0)
);

    setFilteredIssues(filtered);
  }, [issues, searchTerm, statusFilter, categoryFilter, priorityFilter]);

  const getCategoryAnalytics = (issues: Issue[]) => {
  const map: Record<string, number> = {};
  issues.forEach(i => {
    map[i.category] = (map[i.category] || 0) + 1;
  });
  return map;
};

const getStatusAnalytics = (issues: Issue[]) => {
  const map: Record<string, number> = {};
  issues.forEach(i => {
    map[i.status] = (map[i.status] || 0) + 1;
  });
  return map;
};


  const handleStatusChange = (id: string, newStatus: Issue['status']) => {
    const updatedIssue = storageService.updateIssue(id, { status: newStatus });
    if (updatedIssue) {
      loadIssues();
      toast.success(`Issue status updated to ${newStatus.replace('_', ' ')}`);
    }
  };
  const highRiskIssues = getHighRiskIssues(issues);

const getHotspotInsight = () => {
  if (highRiskIssues.length >= 3) {
    return 'Multiple high-risk issues detected in concentrated areas.';
  }
  if (highRiskIssues.length > 0) {
    return 'Isolated high-risk issues detected.';
  }
  return 'No critical urban hotspots currently detected.';
};

  const handleIssueDetails = (issue: Issue) => {
    setSelectedIssue(issue);
    setShowIssueDialog(true);
  };

  const getStatusStats = () => {
    const stats = {
      total: issues.length,
      pending: issues.filter(i => i.status === 'pending').length,
      inProgress: issues.filter(i => i.status === 'in_progress').length,
      resolved: issues.filter(i => i.status === 'resolved').length,
      highPriority: issues.filter(i => i.priority === 'high' && i.status !== 'resolved').length,
      highRisk: issues.filter(i => (i.riskScore ?? 0) >= 70).length,
    };
    return stats;
  };
getCategoryAnalytics
  const getRecentActivity = () => {
    return issues
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 5);
  };

  if (!currentUser || currentUser.role !== 'admin') {
    return null;
  }

  const stats = getStatusStats();
  const recentActivity = getRecentActivity();
  const categoryStats = getCategoryAnalytics(issues);
  const statusStats = getStatusAnalytics(issues);
  const categoryChart = getCategoryChartData(issues);
  const statusChart = getStatusChartData(issues);
  const trendChart = getMonthlyTrendData(issues);


  return (
    <Layout title="Admin Dashboard">
     {highRiskIssues.length > 0 && (
  <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-4 flex items-center justify-between">
    <div className="flex items-center space-x-2 text-red-800">
      <AlertTriangle className="h-5 w-5" />
      <span className="font-medium">
        {highRiskIssues.length} critical issue(s) require immediate attention
      </span>
    </div>
    <Button
      size="sm"
      variant="destructive"
      onClick={() => setFilteredIssues(highRiskIssues)}
    >
      View Critical
    </Button>
  </div>
)}

      <div className="space-y-6 p-6 ">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Issues</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <MapPin className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <User className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Resolved</p>
                <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">High Risk Issues</p>
                <p className="text-2xl font-bold text-red-600">{stats.highRisk}</p>

              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search issues..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="pothole">Pothole</SelectItem>
                  <SelectItem value="street_light">Street Light</SelectItem>
                  <SelectItem value="garbage">Garbage</SelectItem>
                  <SelectItem value="traffic_sign">Traffic Sign</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Issues List/Map */}
            <Tabs defaultValue="list" className="space-y-6">
              <div className="flex justify-between items-center">
                <TabsList>
                  <TabsTrigger value="list">List View</TabsTrigger>
                  <TabsTrigger value="map">Map View</TabsTrigger>
                </TabsList>
                
                <Button onClick={() => navigate('/report')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Report Issue
                </Button>
              </div>
              
              <TabsContent value="list" className="space-y-4">
                {filteredIssues.length === 0 ? (
                  <div className="text-center py-12">
                    <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No issues found</h3>
                    <p className="text-gray-600">No issues match your current filters.</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {filteredIssues.map((issue) => (
                      <IssueCard
                        key={issue.id}
                        issue={issue}
                        onViewDetails={handleIssueDetails}
                        onStatusChange={handleStatusChange}
                        showActions={true}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
              <div className="flex gap-3 text-xs mb-2">
  <span className="flex items-center gap-1">🔴 High Risk</span>
  <span className="flex items-center gap-1">🟡 Medium Risk</span>
  <span className="flex items-center gap-1">🟢 Low Risk</span>
</div>
<Card>
  <CardHeader>
    <CardTitle>Analytics Dashboard</CardTitle>
  </CardHeader>
  <CardContent className="grid grid-cols-3 gap-4 mb-6">
  <div className="rounded-lg border p-3 bg-red-50">
    <p className="text-xs text-muted-foreground">High Risk</p>
    <p className="text-xl font-bold text-red-600">{stats.highRisk}</p>
  </div>

  <div className="rounded-lg border p-3 bg-yellow-50">
    <p className="text-xs text-muted-foreground">Pending</p>
    <p className="text-xl font-bold text-yellow-600">{stats.pending}</p>
  </div>

  <div className="rounded-lg border p-3 bg-green-50">
    <p className="text-xs text-muted-foreground">Resolved</p>
    <p className="text-xl font-bold text-green-600">{stats.resolved}</p>
  </div>
</CardContent>


  <CardContent className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    {/* Category Chart */}
    <div>
      <p className="text-sm font-medium mb-2">Issues by Category</p>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={categoryChart}>
          <XAxis dataKey="name" />
          <YAxis allowDecimals={false} />
           <Tooltip
  contentStyle={{
    backgroundColor: "#111827",
    borderRadius: "8px",
    border: "none",
    color: "#fff",
  }}
  cursor={{ fill: "rgba(0,0,0,0.05)" }}
/>
          <Bar
  dataKey="value"
  radius={[4, 4, 0, 0]}
  fill="#3b82f6"
  label={{ position: "top", fontSize: 10 }}
/>

        </BarChart>
      </ResponsiveContainer>
    </div>

    {/* Status Chart */}
    <div>
      <p className="text-sm font-medium mb-2">Issues by Status</p>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={statusChart}>
          <XAxis dataKey="name" />
          <YAxis allowDecimals={false} />
           <Tooltip
  contentStyle={{
    backgroundColor: "#111827",
    borderRadius: "8px",
    border: "none",
    color: "#fff",
  }}
  cursor={{ fill: "rgba(0,0,0,0.05)" }}
/>
          <Bar dataKey="value" fill="#10b981" radius={[4,4,0,0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>

    {/* Trend Chart */}
    <div>
      <p className="text-sm font-medium mb-2">Monthly Issue Trend</p>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={trendChart}>
          <XAxis dataKey="name" />
          <YAxis allowDecimals={false} />
           <Tooltip
  contentStyle={{
    backgroundColor: "#111827",
    borderRadius: "8px",
    border: "none",
    color: "#fff",
  }}
  cursor={{ fill: "rgba(0,0,0,0.05)" }}
/>
          <CartesianGrid strokeDasharray="3 3" />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#f59e0b"
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </CardContent>
</Card>

<Card>
  <CardHeader>
    <CardTitle>Analytics Overview</CardTitle>
  </CardHeader>

  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {/* Category Analytics */}
    <div>
      <h4 className="text-sm font-semibold mb-2">Issues by Category</h4>
      <div className="space-y-2">
        {Object.entries(categoryStats).map(([cat, count]) => (
          <div key={cat} className="flex justify-between text-sm">
            <span className="capitalize">{cat.replace('_', ' ')}</span>
            <span className="font-medium">{count}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Status Analytics */}
    <div>
      <h4 className="text-sm font-semibold mb-2">Issues by Status</h4>
      <div className="space-y-2">
        {Object.entries(statusStats).map(([status, count]) => (
          <div key={status} className="flex justify-between text-sm">
            <span className="capitalize">{status.replace('_', ' ')}</span>
            <span className="font-medium">{count}</span>
          </div>
        ))}
      </div>
    </div>
  </CardContent>
</Card>


              <TabsContent value="map">
                <IssueMap
                  issues={filteredIssues}
                  selectedIssue={selectedIssue}
                  onIssueSelect={(issue) => {
                    setSelectedIssue(issue);
                    handleIssueDetails(issue);
                  }}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {recentActivity.map((issue) => (
                  <div key={issue.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {issue.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {issue.reportedBy} • {format(issue.updatedAt, 'MMM dd')}
                      </p>
                    </div>
                    <Badge 
                      className={`text-xs ${
                        issue.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        issue.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}
                    >
                      {issue.status.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
             
            <Card>
  <CardHeader>
    <CardTitle className="text-lg">Urban Risk Insight</CardTitle>
  </CardHeader>
  <CardContent className="text-sm text-gray-700">
    🧠 {getHotspotInsight()}
    <p className="text-xs text-muted-foreground mt-2">
      Prototype hotspot detection based on risk concentration
    </p>
  </CardContent>
</Card>


            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setStatusFilter('pending')}
                >
                  <Clock className="h-4 w-4 mr-2" />
                  View Pending Issues ({stats.pending})
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setPriorityFilter('high')}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  High Priority Issues ({stats.highPriority})
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/report')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Issue
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Issue Details Dialog */}
        <Dialog open={showIssueDialog} onOpenChange={setShowIssueDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            {selectedIssue && (
              <>
                <DialogHeader>
                 <DialogTitle className="flex items-center justify-between gap-2">
  <span className="text-lg font-semibold">
    {selectedIssue.title}
  </span>

  <div className="flex items-center gap-2">
    {selectedIssue.riskScore !== undefined && (
      <Badge className="bg-red-100 text-red-700">
        Risk {selectedIssue.riskScore}/100
      </Badge>
    )}
 
    <Badge
      className={
        selectedIssue.status === 'pending'
          ? 'bg-yellow-100 text-yellow-800'
          : selectedIssue.status === 'in_progress'
          ? 'bg-blue-100 text-blue-800'
          : 'bg-green-100 text-green-800'
      }
    >
      {selectedIssue.status.replace('_', ' ')}
    </Badge>
  </div>
</DialogTitle>

                </DialogHeader>
                
                <div className="space-y-6">
                  {selectedIssue.image && (
                    <img
                      src={selectedIssue.image}
                      alt={selectedIssue.title}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  )}
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-600">Category:</span>
                      <p className="capitalize">{selectedIssue.category.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Priority:</span>
                      <p className="capitalize flex items-center space-x-1">
                        {selectedIssue.priority === 'high' && <AlertTriangle className="h-4 w-4 text-red-500" />}
                        <span>{selectedIssue.priority}</span>
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Reported By:</span>
                      <p>{selectedIssue.reportedBy}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Reported On:</span>
                      <p>{format(selectedIssue.reportedAt, 'MMM dd, yyyy HH:mm')}</p>
                    </div>
                  </div>
                  
                  <div>
                    <span className="font-medium text-gray-600">Description:</span>
                    <p className="mt-1">{selectedIssue.description}</p>
                  </div>
                  
                  <div>
                    <span className="font-medium text-gray-600">Location:</span>
                    <p className="mt-1 flex items-center space-x-1">
                      <MapPin className="h-4 w-4" />
                      <span>{selectedIssue.location.address || `${selectedIssue.location.lat}, ${selectedIssue.location.lng}`}</span>
                    </p>
                  </div>

                  {/* Admin Actions */}
                  <div className="flex space-x-2 pt-4 border-t">
                    {selectedIssue.status === 'pending' && (
                      <Button
                        onClick={() => {
                          handleStatusChange(selectedIssue.id, 'in_progress');
                          setShowIssueDialog(false);
                        }}
                      >
                        Start Work
                      </Button>
                    )}
                    {selectedIssue.status === 'in_progress' && (
  <Button
    onClick={() => {
      handleStatusChange(selectedIssue.id, 'resolved');
      setShowIssueDialog(false);
      setShowFeedback(true); // ✅ OPEN FEEDBACK
    }}
  >
    Mark Resolved
  </Button>
)}

                    <Button variant="outline" onClick={() => setShowIssueDialog(false)}>
                      Close
                    </Button>
                  </div>
                </div>
              </>
            )}
            
          </DialogContent>
        </Dialog>
        {selectedIssue && (
  <IssueFeedbackModal
    issue={selectedIssue}
    open={showFeedback}
    onClose={() => {
      setShowFeedback(false);
      loadIssues();
    }}
  />
)}
      </div>
    </Layout>
  );
}